import { Dispatch, SetStateAction, useMemo } from "react";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import { Box, Switch, Typography } from "@mui/material";
import { AccountTypeEnum } from "@rentablo/api/apiv1";
import { Banner } from "@rentablo/components";
import { Chips, FilterItem, FilterValue } from "@components/filters";
import { MandatorConditional } from "@helpers";
import { accountTypeIconMapper } from "@icons/utils";

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

interface Props {
  groups: AccountTypeEnum[];
  selectedChips: FilterValue[];
  setSelectedChips: Dispatch<SetStateAction<FilterValue[]>>;
  hiddenAccountsCount: number;
  showHidden: boolean;
  setShowHidden: Dispatch<SetStateAction<boolean>>;
  showControls?: boolean;
}

export const AccountsListFilters = ({
  groups,
  selectedChips,
  setSelectedChips,
  hiddenAccountsCount,
  showHidden,
  setShowHidden,
  showControls = true,
}: Props) => {
  const { t } = useTranslation();

  const categoryOptions = useMemo<FilterItem[]>(
    () =>
      ["all", ...groups].map((i) => ({
        text: t(`depotsAccountsPage.chips.${i}`),
        value: i,
        clickable: true,
        icon: accountTypeIconMapper[i as AccountTypeEnum],
      })),
    [groups, t]
  );

  return (
    <>
      <MandatorConditional showFor={["kpmg-germany"]}>
        <Box my={6}>
          <Banner
            type="info"
            displayDefaultIcon
            text={t("depotsAccountsPage.banner")}
          />
        </Box>
      </MandatorConditional>
      {showControls && (
        <MandatorConditional hideFor={["kpmg-germany"]}>
          <Box my={6}>
            <Chips
              options={categoryOptions}
              selectedOptions={selectedChips}
              setSelectedOptions={setSelectedChips}
            />
          </Box>
          {hiddenAccountsCount > 0 && (
            <Wrapper>
              <Switch
                checked={showHidden}
                color="primary"
                data-testid="hidden-switch"
                onChange={() => setShowHidden((prev) => !prev)}
                sx={{ marginRight: 3 }}
                size="medium"
              />
              <Typography variant="body1" fontWeight={600}>
                {t("depotsAccountsPage.showHiddenItems")}: {hiddenAccountsCount}
              </Typography>
            </Wrapper>
          )}
        </MandatorConditional>
      )}
    </>
  );
};
